import React from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import type { WebhookInstallStatus } from '../types';

const STATUS_COPY: Record<
  WebhookInstallStatus,
  { label: string; tone: 'neutral' | 'success' | 'warning' | 'error' }
> = {
  draft: { label: 'Not installed yet', tone: 'neutral' },
  installing: { label: 'Installing subscription…', tone: 'warning' },
  active: { label: 'Subscription active', tone: 'success' },
  error: { label: 'Install failed', tone: 'error' },
  archived: { label: 'Archived', tone: 'neutral' },
};

export const WebhookInstallStatusBanner: React.FC<{
  installStatus?: WebhookInstallStatus;
  installError?: { code?: string; message?: string } | null;
}> = ({ installStatus, installError }) => {
  if (installStatus === undefined) {
    return null;
  }
  const copy = STATUS_COPY[installStatus];
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2',
        copy.tone === 'success' && 'border-emerald-500/30 bg-emerald-500/5',
        copy.tone === 'warning' && 'border-amber-500/30 bg-amber-500/5',
        copy.tone === 'error' && 'border-destructive/30 bg-destructive/5',
        copy.tone === 'neutral' && 'border-border bg-muted/20'
      )}
    >
      {installStatus === 'installing' ? (
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
      ) : installStatus === 'active' ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
      ) : installStatus === 'error' ? (
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
      ) : null}
      <div className="min-w-0">
        <Text.P className="text-sm font-medium">{copy.label}</Text.P>
        {installError?.message !== undefined && installError.message !== '' ? (
          <Text.Caption className="text-muted-foreground mt-0.5 block">
            {installError.message}
          </Text.Caption>
        ) : null}
      </div>
    </div>
  );
};
